import { useState } from 'react';
import { ArrowLeft, CheckCircle, Mail } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { SupabaseStatus } from '../common/SupabaseStatus';
import { requestPasswordReset } from '../../services/passwordService';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signIn } = useAuth();

  // Forgot password state
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [forgotSent, setForgotSent] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await signIn(email, password);
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  }

  async function handleForgotPassword(e: React.FormEvent) {
    e.preventDefault();
    setForgotError('');
    setForgotLoading(true);

    try {
      const result = await requestPasswordReset(forgotEmail);
      if (result.success) {
        setForgotSent(true);
      } else {
        setForgotError(result.error || 'Failed to send reset email');
      }
    } catch (err: any) {
      setForgotError(err.message || 'An error occurred');
    } finally {
      setForgotLoading(false);
    }
  }

  function backToLogin() {
    setShowForgotPassword(false);
    setForgotEmail('');
    setForgotError('');
    setForgotSent(false);
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4 relative">
      {/* Supabase Status in top-right corner */}
      <div className="absolute top-4 right-4">
        <SupabaseStatus />
      </div>

      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="flex flex-col items-center justify-center mb-8">
            <img
              src="/logo.png"
              alt="CS NATIONAL Logo"
              className="w-40 h-40 object-contain mb-2"
            />
            <h1 className="text-2xl font-bold text-slate-900">CS NATIONAL</h1>
            <p className="text-sm text-slate-600 mt-1">Trucking Management System</p>
          </div>

          {!showForgotPassword ? (
            <>
              <h2 className="text-xl font-semibold text-center mb-6 text-slate-800">
                Sign In to Your Account
              </h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Password
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                  <div className="text-right mt-1">
                    <button
                      type="button"
                      onClick={() => {
                        setShowForgotPassword(true);
                        setForgotEmail(email);
                      }}
                      className="text-sm text-blue-600 hover:text-blue-700 hover:underline"
                    >
                      Forgot password?
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Signing In...' : 'Sign In'}
                </button>
              </form>

              <p className="mt-6 text-center text-sm text-slate-600">
                Need an account? Contact your administrator.
              </p>
            </>
          ) : (
            <>
              <h2 className="text-xl font-semibold text-center mb-2 text-slate-800">
                Reset Your Password
              </h2>

              {!forgotSent ? (
                <>
                  <p className="text-sm text-slate-600 text-center mb-6">
                    Enter your email and we'll send you a link to reset your password.
                  </p>

                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Email
                      </label>
                      <input
                        type="email"
                        value={forgotEmail}
                        onChange={(e) => setForgotEmail(e.target.value)}
                        className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="you@company.com"
                        required
                        autoFocus
                      />
                    </div>

                    {forgotError && (
                      <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
                        {forgotError}
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={forgotLoading}
                      className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      <Mail className="w-4 h-4" />
                      {forgotLoading ? 'Sending...' : 'Send Reset Link'}
                    </button>
                  </form>
                </>
              ) : (
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-green-800 mb-1">Check your email</p>
                      <p className="text-sm text-green-700">
                        If an account exists for <strong>{forgotEmail}</strong>, a password reset
                        link is on its way. The link expires in 1 hour — check your spam folder
                        if you don't see it.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <button
                type="button"
                onClick={backToLogin}
                className="mt-6 w-full flex items-center justify-center gap-2 text-sm text-slate-600 hover:text-slate-900"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Sign In
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
